import { useEffect, useState } from "react";
import Widget from "../_components/Widget";
import { useLang } from "../_providers/LangProvider";
import ThemeController from "../controllers/ThemeController";
import { useAuth } from "../_providers/AuthProvider";
import Theme from "../models/Theme";
import UserController from "../controllers/UserController";
import { useModal } from "../_providers/ModalProvider";
import SingleInputModal from "../_modals/SingleInputModal";
import { useTheme } from "../_providers/ThemeProvider";

         

const ThemesPage = () => {
    const {user, reloadUser} = useAuth();
    const {translate} = useLang();
    const {setModal, closeModal} = useModal();
    const {reloadTheme, baseTheme} = useTheme();

    const [colors, setColors] = useState(baseTheme.colors);
    const [font, setFont] = useState(baseTheme.font);

    const [validFont, setValidFont] = useState(false);
    const [loadingFont, setLoadingFont] = useState(false);
    const [errorFont, setErrorFont] = useState("");

    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [themes, setThemes] = useState<Theme[]>([]);

    const onColorChange = (key: string, color: string) => {
        setColors({...colors, [key]: color})
    }

    const getCurrentTheme = () => {
        if(!user) {
            setError("Not an authorized user");
            return;
        }
        ThemeController.getThemes(user.uid).then(res => {
            console.log(res);
            setLoading(false);
            setThemes(res);
            const currTheme = res.find((theme: Theme) => theme.uid == user.selectedTheme);
            if(currTheme) {
                setFont(currTheme.font);
                setColors(currTheme.colors);
            } else {
                setFont(baseTheme.font);
                setColors(baseTheme.colors);
            }
        }).catch(err => {
            setError("Failed to get theme");
            setLoading(false);
        });
    }

    useEffect(() => {
        if(!user) {
            setError("Not an authorized user");
            return;
        }
        getCurrentTheme();
    }, [user]);

    const saveChanges = () => {
        if (!font) {
            setError("Font is required");
            return;
        }
        if (!colors) {
            setError("Colors are required");
            return;
        }
        if (!user || !user.selectedTheme) {
            setError("No theme selected");
            return;
        }
        verifyGoogleFont(font).then(_ => {
            setError("");
            if (!user || !user.selectedTheme) {
                setError("No theme selected");
                return;
            }
            ThemeController.updateTheme(user.selectedTheme, font, colors).then(res => {
                reloadTheme();
                setError("");
            }).catch(err => {
                setError("Failed to update theme");
            });
        }).catch(err => {
            setError("Failed to verify font");
        });
    }
        
    const verifyGoogleFont = async (font: string) => {
        return await fetch(`https://fonts.googleapis.com/css2?family=${font.split(" ").join("+")}`);
    }

    const onVerify = (font: string) => {
        setLoadingFont(true);
        setValidFont(false);
        verifyGoogleFont(font).then(res => {
           res.text().then(text => {
               console.log(text);
               setErrorFont("");
               setLoadingFont(false);
               setValidFont(true);
           }).catch(err => {setErrorFont(new Date().toLocaleTimeString("nl-NL")); setLoadingFont(false)});
        }).catch(err => {setErrorFont(new Date().toLocaleTimeString("nl-NL")); setLoadingFont(false)});
    }

    const onSelectTheme = (theme_id: string) => {
        if(!user) {
            setError("Not an authorized user");
            return;
        }
        if(theme_id == "new") {
            setModal("New Theme", <SingleInputModal onCreate={(name: string) => {
                ThemeController.createTheme(user.uid, name, font, colors).then(theme => {
                    setThemes([...themes, theme]);
                    setError("");
                    closeModal();
                }).catch(err => {
                    console.log(error)
                    setError("Failed to create new theme");
                });
            }} />);
            return;
        }

        if(theme_id == "default") {
            UserController.setSelectedTheme(user.uid, undefined).then(_ => {
                setError("");
                setFont(baseTheme.font);
                setColors(baseTheme.colors);
                reloadUser();
                reloadTheme();
            }).catch(err => {
                setError("Failed to set default theme");
            });
            return;
        }
        console.log(theme_id);
        
        const theme = themes.find((theme: Theme) => theme.uid == theme_id);
        if (!theme) {
            setError("Theme not found");
            return;
        }
        UserController.setSelectedTheme(user.uid, theme.uid).then(_ => {
            setError("");
            setFont(theme.font);
            setColors(theme.colors);
            reloadUser();
            reloadTheme();
        }).catch(err => {
            setError("Failed to set selected theme");
        });

    }

    const importTheme = () => {
        window.navigator.clipboard.readText().then(text => {
            const theme = JSON.parse(text);
            if(!theme.font || !theme.colors) {
                setError("Invalid theme format");
                return;
            }
            setFont(theme.font);
            setColors(theme.colors);
            setError("");
        }).catch(err => {
            setError("Failed to import theme");
        });
    }

    const exportTheme = () => {
        window.navigator.clipboard.writeText(JSON.stringify({font, colors})).then(() => {
            alert("Theme copied to clipboard");
        }).catch(err => {
            setError("Failed to copy theme to clipboard");
        });
    }

    if(error) return <div className="error">{error}</div>;
    if(loading) return <div className="loading">Loading...</div>;
    return (
        <div style={{paddingBottom: 50}}>
            <h1>{translate("themes_nav")}</h1>
            <div style={{display: "flex", flexDirection: "row", flexWrap: "wrap", gap: 10}}>
                <Widget title={translate("select_theme")} flex={2} minWidth="500px" maxWidth="550px">
                    <select value={user?.selectedTheme ? user.selectedTheme : "default"} onChange={(e) => onSelectTheme(e.target.value)}>
                        {themes && themes.map((theme: Theme) => (
                            <option key={theme.uid} value={theme.uid}>{theme.name}</option>
                        ))}
                        <option value="default">Default Theme</option>
                        <option value="new">New Theme<i className="fa-solid fa-plus"/></option>
                    </select>
                    <label style={{fontSize: 10}}>The selected theme is also activated automatically for the user</label>
                    <div style={{display: "flex", gap: 10, marginTop: 10}}>
                        <button disabled={!user?.selectedTheme} className={user?.selectedTheme ? "btn" : "btn cancel-btn"} style={{width: "max-content"}} onClick={saveChanges}><p style={{textAlign: "center", width: "100%"}}>Save Changes</p><i style={{padding: "0 10px 0 10px"}} className="fa-solid fa-save"/></button>
                        <button disabled={!user?.selectedTheme} className={user?.selectedTheme ? "btn" : "btn cancel-btn"} style={{width: "max-content"}} onClick={importTheme}><p style={{textAlign: "center", width: "100%"}}>Import Theme</p><i style={{padding: "0 10px 0 10px"}} className="fa-solid fa-file-import"/></button>
                        <button className={"btn"} style={{width: "max-content"}} onClick={exportTheme}><p style={{textAlign: "center", width: "100%"}}>Export Theme</p><i style={{padding: "0 10px 0 10px"}} className="fa-solid fa-file-export"/></button>
                    </div>

                </Widget>
                {user?.selectedTheme ? 
                <>
                    <Widget title={translate("set_font")} flex={1} minWidth="400px">
                        <input type="text" value={font} onChange={e => setFont(e.target.value)} placeholder="Open Sans" />
                        <label style={{fontSize: 10, color: validFont ? "green" :  loadingFont ? "var(--primary-color)" : errorFont ? "red" : "inherit"}}>{validFont ? "Valid font!" : loadingFont ? "Verifying..." : "The font should be a Google Api font " + (errorFont ? " | Last attempt: " + errorFont : "")}</label>
                        <button className="btn" style={{width: "max-content"}} onClick={() => onVerify(font)}><p style={{textAlign: "center", width: "100%"}}>Verify</p><i style={{padding: "0 10px 0 10px"}} className="fa-solid fa-refresh"/></button>
                    </Widget>
                    <Widget title={translate("set_colors")} flex={1} minWidth="300px">
                        <div style={{display: "flex", flexDirection: "column", gap: "0px"}}>
                            {colors && Object.entries(colors).sort().map(([key, value]) => (
                                <div className="transaction dark" key={key}>
                                    <div style={{display: "flex", gap: 20, alignItems: "center"}}>
                                        <input type="color" id="favcolor" value={value} onChange={(e) => onColorChange(key, e.target.value)}/>
                                        <label htmlFor="favcolor">{key.slice(2, key.length).replaceAll("-", " ")}</label>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Widget>
                </>
                : null}
            </div>
        </div>
    );
}

export default ThemesPage;
